import React, { useEffect, useState, useMemo } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import EventFilledIcon from "@mui/icons-material/Event";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NoteAddOutlined from "@mui/icons-material/NoteAddOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import BuildOutlined from "@mui/icons-material/BuildOutlined";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import PriorityHighOutlined from "@mui/icons-material/PriorityHighOutlined";
import {
  ChangeServiceReport,
  ServiceReportFile,
  ServiceReportFileTypeCode,
  ServiceReportsState,
} from "../models/ServiceReportsModel";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateField, DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  changeServiceReport,
  createServiceReport,
  getMachineParts,
  getServiceReports,
} from "../customerMachineServiceReportsSlice";
import ServiceReportUploadFiles from "./ServiceReportUploadFile";
import { dt } from "../utils/dt";
import { accentGradientDeep } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import useTranslation from "../../../shared/hooks/useTranslation";

export interface ServiceReportDialogProps {
  open: boolean;
  onClose: () => void;
  machineId: string;
}

/* ── Section header shared style ── */
const sectionHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 1,
};

const sectionLabelSx = {
  fontFamily: dt.font,
  fontSize: "12px",
  fontWeight: 700,
  color: dt.textSecondary,
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
};

const sectionIconSx = {
  fontSize: 16,
  color: dt.textMuted,
};

interface PartOption {
  label: string;
  group: string;
  partId: string;
}

const ServiceReportDialog = ({ open, onClose, machineId }: ServiceReportDialogProps) => {
  useRedesignSetup();
  const { serviceReport, serviceReportsQuery, machineParts } = useAppSelector<ServiceReportsState>(
    (state: any) => state.customerServiceReports
  );
  const dispatch = useAppDispatch();
  const isPhone = useMediaQuery("(max-width:768px)");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const {
    register,
    watch,
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<ChangeServiceReport>();

  const openDate = watch("openDate");

  const onSubmit: SubmitHandler<ChangeServiceReport> = (data: ChangeServiceReport) => {
    handleCreateServiceReport(data);
  };

  const handleCreateServiceReport = async (model: ChangeServiceReport) => {
    model.openDate = model.openDate || moment().toDate();
    model.files = files;
    if (isNew) {
      await dispatch(createServiceReport({ model, machineId }));
    } else {
      await dispatch(changeServiceReport({ model, serviceReportNumber }));
    }
    onClose();
    await dispatch(getServiceReports({ machineId: machineId, query: serviceReportsQuery }));
  };

  const serviceReportNumber = serviceReport?.number || undefined;
  const isNew: boolean = !serviceReport;
  const [files, setFiles] = useState<ServiceReportFile[] | null>(serviceReport?.files || []);

  const handlerCreateFile = (files: ServiceReportFile[] | null) => {
    setFiles(files);
  };

  // Inject dialog-specific keyframes + textarea styles
  useEffect(() => {
    const styleId = "sr-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes dialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .sr-textarea {
          width: 100%;
          box-sizing: border-box;
          font-family: 'JetBrains Mono', monospace;
          font-size: 14px;
          line-height: 1.6;
          padding: 10px 14px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          background-color: #F4F6F9;
          color: #1F2937;
          resize: vertical;
          outline: none;
          overflow: auto;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .sr-textarea::placeholder {
          color: #9CA3AF;
          font-family: 'Sora', sans-serif;
          font-size: 14px;
        }
        .sr-textarea:hover {
          border-color: #D5DBE1;
        }
        .sr-textarea:focus {
          border-color: #3498DB;
          border-width: 1.5px;
          box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.08);
          background-color: #FFFFFF;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    dispatch(getMachineParts());
    if (serviceReport) {
      const changeServiceReport: ChangeServiceReport = {
        title: serviceReport.title,
        description: serviceReport.description,
        notes: serviceReport.notes || "",
        alarmCode: serviceReport.alarmCode || "",
        urgency: serviceReport.urgency,
        openDate: serviceReport.openDate,
        affectedPartCategoryId: serviceReport.affectedPartCategoryId,
        files: serviceReport.files,
      };
      reset(changeServiceReport);
    } else {
      const createServiceReport: ChangeServiceReport = {
        title: "",
        description: "",
        notes: "",
        alarmCode: "",
        urgency: undefined,
        openDate: undefined,
        affectedPartCategoryId: undefined,
        files: null,
      };
      reset(createServiceReport);
    }
  }, [serviceReport]);

  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const { translate: tServicing } = useTranslation("servicing");

  // Build grouped part options from Machinery API
  const partOptions: PartOption[] = useMemo(() => {
    const opts: PartOption[] = [];
    if (machineParts && machineParts.length > 0) {
      for (const group of machineParts) {
        const groupLabel = tServicing(`machineParts.groups.${group.groupKey}`);
        for (const part of group.parts) {
          const translationKey = part.code.replace(/_([a-z])/g, (_m: string, c: string) => c.toUpperCase());
          const partLabel = tServicing(`machineParts.parts.${translationKey}`);
          opts.push({ label: partLabel, group: groupLabel, partId: part.id });
        }
      }
    }
    return opts;
  }, [machineParts, tServicing]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isPhone}
      PaperProps={{
        sx: {
          borderRadius: isPhone ? 0 : dt.radiusLg,
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "dialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          maxHeight: isPhone ? "100%" : "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...(isPhone && {
            margin: "16px",
            width: "calc(100% - 32px)",
            maxWidth: "none",
            maxHeight: "calc(100% - 32px)",
          }),
        },
      }}
    >
      {/* Blue accent bar */}
      <Box sx={{ height: "4px", background: `linear-gradient(90deg, ${dt.serviceReports.dialogBlueAccent}, ${dt.blueHover})` }} />

      {/* Title with icon circle */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          pb: 0.5,
          pt: 2,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: "rgba(52,152,219,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <NoteAddOutlined sx={{ color: dt.blue, fontSize: 22 }} />
        </Box>
        <Box
          sx={{
            fontWeight: 700,
            fontSize: { xs: "18px", sm: "20px" },
            color: dt.textPrimary,
            letterSpacing: "-0.3px",
          }}
        >
          {isNew ? translate("createServiceReport") : translate("editServiceReport")}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2, fontFamily: dt.font, overflow: "auto", flexShrink: 1, minWidth: 0 }}>
        <form id="changeServiceReport" onSubmit={handleSubmit(onSubmit)} style={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1, minWidth: 0 }}>
            {/* ── REPORT TITLE section ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <DescriptionOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("reportTitleSection")}</Box>
              </Box>
              <Tooltip title={translate("titleCharLimit")} arrow placement="top-start">
                <TextField
                  fullWidth
                  size="small"
                  placeholder={translate("reportTitle")}
                  {...register("title", {
                    required: tCommon("forms.required"),
                    maxLength: { value: 100, message: translate("titleTooLong") },
                  })}
                  inputProps={{ maxLength: 100 }}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  sx={{ "& .MuiInputBase-input": { fontFamily: dt.fontMono } }}
                />
              </Tooltip>
            </Box>

            {/* ── DATE & TIME section ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <CalendarTodayOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("dateAndTimeSection")}</Box>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                <Box>
                  <InputLabel sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 500 }}>
                    {translate("openDate")}
                  </InputLabel>
                  <FormControl fullWidth size="small">
                    <Controller
                      control={control}
                      name="openDate"
                      rules={{
                        validate: (value) => {
                          if (value) {
                            if (moment(value) > moment()) {
                              return translate("openDateFuture");
                            }
                          }
                        },
                      }}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            {...field}
                            value={moment(field.value)}
                            onChange={(value) => field.onChange(value?.toDate())}
                            format="DD.MM.YYYY"
                            disableFuture
                            maxDate={moment()}
                            views={["year", "month", "day"]}
                            open={isCalendarOpen}
                            onOpen={() => setIsCalendarOpen(true)}
                            onClose={() => setIsCalendarOpen(false)}
                            slotProps={{
                              textField: {
                                size: "small",
                                name: "openDate",
                                InputProps: {
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() => setIsCalendarOpen(true)}
                                        sx={{ color: dt.blue, "&:hover": { backgroundColor: dt.blueSoft } }}
                                      >
                                        <EventFilledIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                },
                              },
                              popper: {
                                sx: {
                                  "& .MuiPickersDay-root": {
                                    "&.Mui-selected": {
                                      backgroundColor: dt.blue,
                                      "&:hover": { backgroundColor: dt.blue },
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <InputLabel sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 500 }}>
                    {tCommon("common.time")}
                  </InputLabel>
                  <FormControl fullWidth size="small">
                    <Controller
                      control={control}
                      name="openDate"
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DateField
                            {...field}
                            value={moment(openDate)}
                            onChange={(value) => field.onChange(value?.toDate())}
                            format="HH:mm"
                            size="small"
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </FormControl>
                </Box>
              </Box>
              <FormHelperText error>{errors.openDate?.message}</FormHelperText>
            </Box>

            {/* ── ALARM CODE section ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <ErrorOutlineOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("alarmCodeSection")}</Box>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder={translate("alarmCodePlaceholder")}
                {...register("alarmCode", {
                  maxLength: { value: 100, message: translate("alarmCodeTooLong") },
                })}
                inputProps={{ maxLength: 100 }}
                error={!!errors.alarmCode}
                helperText={errors.alarmCode?.message}
                sx={{
                  "& .MuiInputBase-input": { fontFamily: dt.fontMono },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: dt.radiusSm,
                    "&.Mui-focused fieldset": { borderColor: dt.blue },
                  },
                }}
              />
            </Box>

            {/* ── URGENCY section ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <PriorityHighOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("urgencySection")}</Box>
              </Box>
              <FormControl fullWidth size="small">
                <Controller
                  control={control}
                  name="urgency"
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                      displayEmpty
                      sx={{
                        fontFamily: dt.font,
                        borderRadius: dt.radiusSm,
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: dt.radiusSm,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                          },
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ fontFamily: dt.font, color: dt.textMuted }}>
                        {translate("urgencyNone")}
                      </MenuItem>
                      <MenuItem value={1} sx={{ fontFamily: dt.font }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#C0392B" }} />
                          {translate("priorities.urgent")}
                        </Box>
                      </MenuItem>
                      <MenuItem value={2} sx={{ fontFamily: dt.font }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#E67E22" }} />
                          {translate("priorities.high")}
                        </Box>
                      </MenuItem>
                      <MenuItem value={4} sx={{ fontFamily: dt.font }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#27AE60" }} />
                          {translate("priorities.low")}
                        </Box>
                      </MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Box>

            {/* ── AFFECTED MACHINE PART section (grouped autocomplete) ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <BuildOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("affectedPartSection")}</Box>
              </Box>
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="affectedPartCategoryId"
                  rules={{ required: tCommon("forms.required") }}
                  render={({ field }) => {
                    const selectedOption = partOptions.find((o) => o.partId === field.value) || null;
                    return (
                      <Autocomplete
                        options={partOptions}
                        groupBy={(option) => option.group}
                        getOptionLabel={(option) => option.label}
                        value={selectedOption}
                        onChange={(_e, newValue) => {
                          field.onChange(newValue?.partId || "");
                          setTimeout(() => trigger("affectedPartCategoryId"));
                        }}
                        isOptionEqualToValue={(option, value) => option.partId === value.partId}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            error={!!errors.affectedPartCategoryId}
                            placeholder={translate("affectedMachinePart")}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: dt.radiusSm,
                                "&.Mui-focused fieldset": { borderColor: dt.blue },
                              },
                            }}
                          />
                        )}
                        renderGroup={(params) => (
                          <li key={params.key}>
                            <Box
                              component="div"
                              sx={{
                                fontFamily: dt.font,
                                fontSize: "11px",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.8px",
                                color: dt.textSecondary,
                                px: 2,
                                pt: 1.5,
                                pb: 0.5,
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#fff",
                                zIndex: 1,
                              }}
                            >
                              {params.group}
                            </Box>
                            <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
                              {params.children}
                            </ul>
                          </li>
                        )}
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            {...props}
                            sx={{
                              fontFamily: `${dt.font} !important`,
                              fontSize: "14px !important",
                              pl: "32px !important",
                              py: "6px !important",
                              "&:hover": { backgroundColor: `${dt.blueSoft} !important` },
                              "&.Mui-focused": { backgroundColor: `${dt.blueSoft} !important` },
                            }}
                          >
                            {option.label}
                          </Box>
                        )}
                        ListboxProps={{
                          sx: { maxHeight: 300, overflow: "auto" },
                        }}
                        slotProps={{
                          paper: {
                            sx: {
                              borderRadius: dt.radiusSm,
                              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                            },
                          },
                        }}
                      />
                    );
                  }}
                />
                <FormHelperText error>{errors.affectedPartCategoryId?.message}</FormHelperText>
              </FormControl>
            </Box>

            {/* ── DESCRIPTION section ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <EditNoteOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("descriptionSection")}</Box>
              </Box>
              <textarea
                className="sr-textarea"
                {...register("description")}
                rows={8}
                maxLength={2000}
                placeholder={translate("descriptionPlaceholder")}
              />
            </Box>

            {/* ── TECHNICAL NOTES section ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <StickyNote2Outlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("technicalNotesSection")}</Box>
              </Box>
              <textarea
                className="sr-textarea"
                {...register("notes")}
                rows={4}
                maxLength={4000}
                placeholder={translate("technicalNotesPlaceholder")}
              />
            </Box>

            {/* ── ATTACHMENTS section ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <AttachFileOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("attachmentsSection")}</Box>
              </Box>
              <ServiceReportUploadFiles
                files={serviceReport?.files || []}
                onChange={handlerCreateFile}
                typeCode={ServiceReportFileTypeCode.reportDocument}
                isCreate={isNew}
                marginTop={0}
              />
            </Box>
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-end", gap: 1.5, pb: 3, px: 3, flexShrink: 0 }}>
        <Button
          variant="text"
          onClick={onClose}
          sx={{
            fontFamily: dt.font,
            fontWeight: 600,
            fontSize: "14px",
            textTransform: "none",
            color: dt.textSecondary,
            borderRadius: dt.radiusSm,
            px: 2.5,
            py: 1,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: dt.inputBg,
              color: dt.textPrimary,
            },
          }}
        >
          {tCommon("buttons.cancel")}
        </Button>

        <Button
          form="changeServiceReport"
          type="submit"
          variant="contained"
          startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "18px !important" }} />}
          sx={{
            background: dt.serviceReports.createBtnGradient,
            borderRadius: dt.radiusPill,
            fontFamily: dt.font,
            fontWeight: 600,
            textTransform: "none",
            px: 3,
            py: 1,
            boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
            "&:hover": {
              background: accentGradientDeep,
              boxShadow: "0 4px 14px rgba(52,152,219,0.35)",
            },
          }}
        >
          {isNew ? tCommon("buttons.createReport") : tCommon("buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceReportDialog;
